import type { TFunction } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import Modal, { ModalButton, ModalFooterLink, ModalSecondaryButton } from "../../components/Modal";
import constants from "../../constants";
import { useAccountCurrency } from "../../hooks/AccountCurrency";
import {
  AGENDA_ERRORS,
  AgendaBooking,
  AgendaBookingPayment,
  AgendaBookingStatus,
  cancelBooking,
  confirmBooking,
  declineBooking,
  errorCodeOf,
  refundBooking,
} from "../../services/agenda";
import { isSafeEmail, whatsappLink } from "../Contacts/helpers";
import { ReplyButton } from "../Contacts/styles";
import { FieldTypeIcon } from "../Editor/pages/Form/components/FieldTypeIcon";
import WhatsappIcon from "../Editor/pages/Sections/icons/Whatsapp";
import { formatCents, formatPhone } from "./format";

const prefix = "pages.agenda.bookings";

type Action = "confirm" | "decline" | "cancel";

/** What the modal shows: the booking, or a question about it (cancel it, refund it, or refund it after a cancel). */
export type BookingStep = "detail" | "cancel" | "refund" | "offerRefund";

/** Each status' color (a dot and its word), on the day's list and in the modal. */
export const STATUS_COLORS: Record<AgendaBookingStatus, string> = {
  confirmed: "var(--color-feedback-success)",
  pending: "#ffb066",
  declined: "#ff8aa0",
  canceled: "var(--color-neutral-150)",
  awaiting_payment: "var(--color-neutral-150)",
  expired: "var(--color-neutral-150)",
  // paid and with no time: the owner has to act (reschedule or refund), stronger than a request's orange
  paid_no_slot: "var(--color-orange)",
};

/** The booking's online payment (only with online payments on; none for one paid on site, as before). */
export const paymentOf = (booking: AgendaBooking): AgendaBookingPayment | null =>
  (constants.payments && booking.payment) || null;

/** The online payment that can still be refunded. */
export const paidOf = (booking: AgendaBooking) => {
  const payment = paymentOf(booking);
  return payment?.status === "paid" ? payment : null;
};

/**
 * Whether who looks can refund it: the API sends what the owner gets (`ownerNetCents`, even when null) only to the
 * Keepo's owner, and the refund is the owner's alone. No other roles in the editor yet: it's the one signal there is.
 */
export const canRefund = (payment: AgendaBookingPayment) => payment.ownerNetCents !== undefined;

/** What can be done about a booking's refund: nothing paid (null), refund it, or it is the owner's to do. */
export const refundOf = (booking: AgendaBooking): "allowed" | "owner-only" | null => {
  const paid = paidOf(booking);
  if (!paid) return null;
  return canRefund(paid) ? "allowed" : "owner-only";
};

/** Online payments are always in reais, whatever the account's currency (as in Cobranças). */
export const PAYMENT_CURRENCY = "BRL";

/** "Pago com Pix — R$ 46,50", "Reembolsado — R$ 46,50", "Pagamento contestado — R$ 46,50" (nothing paid: no line). */
export const paymentLine = (booking: AgendaBooking, t: TFunction) => {
  const payment = paymentOf(booking);
  if (!payment) return null;
  const amount = formatCents(payment.amountCents, PAYMENT_CURRENCY);
  if (payment.status === "paid") {
    const method = payment.method === "pix" || payment.method === "card" ? payment.method : "paid";
    return t(`${prefix}.payment.${method}`, { amount });
  }
  if (payment.status === "refunded") return t(`${prefix}.payment.refunded`, { amount });
  // the customer disputed it with their bank: nothing to refund, and withdrawals wait for the dispute
  if (payment.status === "chargeback") return t(`${prefix}.payment.chargeback`, { amount });
  // nothing paid (yet): the status says it all
  return null;
};

const CHANGES: Record<Action, typeof confirmBooking> = {
  confirm: confirmBooking,
  decline: declineBooking,
  cancel: cancelBooking,
};

type Props = {
  siteId: string;
  booking: AgendaBooking;
  isPlus: boolean;
  /** `reload`: other bookings changed with it (refunding the old booking of a reschedule cancels the new one too). */
  onChanged: (booking: AgendaBooking, reload?: boolean) => void;
  /** It isn't what it was (answered already, by the link in the WhatsApp or the e-mail): the list is read again. */
  onOutdated: () => void;
  /** Change it (service, day, time, the customer's data); a paid one with no time is rescheduled with it. */
  onEdit: () => void;
  onExit: () => void;
  /** Opens on a question: refund it (from the list), or refund it now that it's declined (the booking as it is now). */
  step?: BookingStep;
};

/**
 * A booking: when, who (with WhatsApp and e-mail to answer, as a contact), and everything filled in. A waiting one is
 * confirmed or declined; one that holds its time is canceled after asking, in the same modal. One paid online shows how,
 * and is refunded after asking (also right after it's canceled or declined, which don't give the money back). Each
 * action shows it's working until the API answers.
 */
export const BookingModal = ({
  siteId,
  booking: initial,
  isPlus,
  onChanged,
  onOutdated,
  onEdit,
  onExit,
  step: initialStep = "detail",
}: Props) => {
  const { t, i18n } = useTranslation();
  const { currency } = useAccountCurrency();
  const [working, setWorking] = useState<Action | "refund" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<BookingStep>(initialStep);
  // canceled or declined here, waiting for the answer about the refund
  const [changedHere, setChangedHere] = useState<AgendaBooking | null>(null);
  const booking = changedHere ?? initial;
  const paid = paidOf(booking);

  const act = async (action: Action) => {
    setWorking(action);
    setError(null);
    try {
      const result = await CHANGES[action](siteId, booking.id);
      // canceling or declining doesn't give the money back: the owner is asked
      if (action !== "confirm" && paid) {
        setChangedHere(result);
        setStep("offerRefund");
        setWorking(null);
        return;
      }
      onChanged(result);
    } catch (error) {
      if (errorCodeOf(error) === AGENDA_ERRORS.notAllowed) {
        onOutdated();
        return;
      }
      setError(t(`${prefix}.changeError`));
      setWorking(null);
    }
  };

  const refund = async () => {
    setWorking("refund");
    setError(null);
    try {
      onChanged(await refundBooking(siteId, booking.id), true);
    } catch (error) {
      const code = errorCodeOf(error);
      // refunded already (or never paid): what's on screen is old
      if (code === AGENDA_ERRORS.notRefundable) {
        onOutdated();
        return;
      }
      setError(t(`${prefix}.refundErrors.${code === AGENDA_ERRORS.insufficientBalance ? "balance" : "other"}`));
      setWorking(null);
    }
  };

  // the list hears of a change made here, however the modal closes
  const exit = () => (changedHere ? onChanged(changedHere) : onExit());

  // "not now": back to the booking, or out when the modal opened on the question
  const back = () => {
    setError(null);
    if (step === "offerRefund" || initialStep !== "detail") exit();
    else setStep("detail");
  };

  const whatsapp = whatsappLink(booking.customer.phone);
  const email = booking.customer.email && isSafeEmail(booking.customer.email) ? booking.customer.email : null;
  const price = formatCents(booking.service.priceCents, currency);
  const payment = paymentLine(booking, t);
  const day = new Intl.DateTimeFormat(i18n.language, {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(new Date(`${booking.date}T12:00:00Z`));

  const rows = (
    [
      [t(`${prefix}.service`), booking.service.name],
      ...(price
        ? [
            [t(`${prefix}.price`), booking.service.priceFrom ? t(`${prefix}.priceFrom`, { price }) : price] as [
              string,
              string,
            ],
          ]
        : []),
      ...(payment ? [[t(`${prefix}.payment.label`), payment] as [string, string]] : []),
      ...(paid && paid.ownerNetCents != null
        ? [[t(`${prefix}.payment.net`), formatCents(paid.ownerNetCents, PAYMENT_CURRENCY)] as [string, string]]
        : []),
      // paid, but not by who looks to refund: says why the button is off
      ...(paid && !canRefund(paid)
        ? [[t(`${prefix}.refundLabel`), t(`${prefix}.refundOwnerOnly`)] as [string, string]]
        : []),
      [t(`${prefix}.phone`), formatPhone(booking.customer.phone)],
      ...(booking.customer.email ? [[t(`${prefix}.email`), booking.customer.email] as [string, string]] : []),
      ...booking.fields.map(field => [field.label || field.type, field.value] as [string, string]),
    ] as [string, string | null | undefined][]
  )
    // what wasn't filled in has no row
    .filter((row): row is [string, string] => !!row[1]?.trim());

  // a quiet link on the footer's left, as in Cobranças: there when needed, apart from the booking's actions
  const refundButton = paid && (
    <ModalFooterLink type="button" disabled={!!working || !canRefund(paid)} onClick={() => setStep("refund")}>
      {t(`${prefix}.refund`)}
    </ModalFooterLink>
  );

  // Whatever the plan, the customers hear of an answer or a cancellation; booking and editing are PLUS
  const footer =
    step === "refund" || step === "offerRefund" ? (
      <>
        <ModalSecondaryButton type="button" disabled={!!working} onClick={back}>
          {t(`${prefix}.notNow`)}
        </ModalSecondaryButton>
        <ModalButton loading={working === "refund"} onClick={refund}>
          {t(`${prefix}.refund`)}
        </ModalButton>
      </>
    ) : step === "cancel" ? (
      <>
        <ModalSecondaryButton type="button" disabled={!!working} onClick={() => setStep("detail")}>
          {t(`${prefix}.keep`)}
        </ModalSecondaryButton>
        <ModalButton variant="DANGER" loading={working === "cancel"} onClick={() => act("cancel")}>
          {t(`${prefix}.cancel`)}
        </ModalButton>
      </>
    ) : booking.status === "paid_no_slot" ? (
      // paid, but its time was lost: another time (it's confirmed, with no new charge), or the money back
      <>
        {refundButton}
        {isPlus && <ModalButton onClick={onEdit}>{t(`${prefix}.reschedulePaid`)}</ModalButton>}
      </>
    ) : booking.status === "pending" ? (
      <>
        {refundButton}
        {isPlus && (
          <ModalSecondaryButton type="button" disabled={!!working} onClick={onEdit}>
            {t(`${prefix}.edit`)}
          </ModalSecondaryButton>
        )}
        <ModalSecondaryButton type="button" disabled={!!working} onClick={() => act("decline")}>
          {working === "decline" ? t(`${prefix}.declining`) : t(`${prefix}.decline`)}
        </ModalSecondaryButton>
        <ModalButton loading={working === "confirm"} disabled={!!working} onClick={() => act("confirm")}>
          {t(`${prefix}.confirm`)}
        </ModalButton>
      </>
    ) : booking.status === "confirmed" ? (
      <>
        {refundButton}
        <ModalSecondaryButton type="button" onClick={() => setStep("cancel")}>
          {t(`${prefix}.cancel`)}
        </ModalSecondaryButton>
        {isPlus && <ModalButton onClick={onEdit}>{t(`${prefix}.edit`)}</ModalButton>}
      </>
    ) : // canceled or declined and still paid: refunded later; waiting for the payment: nothing to do yet
    booking.status !== "awaiting_payment" && paid ? (
      <ModalButton disabled={!canRefund(paid)} onClick={() => setStep("refund")}>
        {t(`${prefix}.refund`)}
      </ModalButton>
    ) : undefined;

  const asking =
    step === "cancel"
      ? t(`${prefix}.cancelText`)
      : t(`${prefix}.refundText`, { amount: formatCents(paid?.amountCents, PAYMENT_CURRENCY) });
  const title = {
    detail: booking.customer.name,
    cancel: t(`${prefix}.cancelTitle`),
    refund: t(`${prefix}.refundTitle`),
    offerRefund: t(`${prefix}.refundOfferTitle`),
  }[step];

  return (
    <Modal title={title} escapeExit busy={!!working} onExit={exit} error={error ?? undefined} footer={footer}>
      {step !== "detail" ? (
        <Asking>
          {asking}
          <strong>
            {day}, {booking.time} · {booking.customer.name}
          </strong>
        </Asking>
      ) : (
        <Body>
          <When>
            <span className="time">
              <strong>{booking.time}</strong>
              <small>{t("pages.agenda.services.minutes", { count: booking.service.durationMinutes })}</small>
            </span>
            <span className="text">
              <span className="day">{day}</span>
              <Status $status={booking.status}>{t(`${prefix}.statuses.${booking.status}`)}</Status>
              {booking.replacesId && <span className="note">{t(`${prefix}.reschedule`)}</span>}
            </span>
          </When>

          {(whatsapp || email) && (
            <div className="answer">
              {whatsapp && (
                <ReplyButton href={whatsapp} target="_blank" rel="noopener noreferrer" $primary>
                  <WhatsappIcon width={18} height={18} />
                  WhatsApp
                </ReplyButton>
              )}
              {email && (
                <ReplyButton href={`mailto:${email}`}>
                  <FieldTypeIcon type="EMAIL" size={18} />
                  {t(`${prefix}.email`)}
                </ReplyButton>
              )}
            </div>
          )}

          <Rows>
            {rows.map(([label, value], index) => (
              <div key={index} className="row">
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </Rows>
        </Body>
      )}
    </Modal>
  );
};

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  font-family: Inter, sans-serif;

  /* two columns of the same width: alone, a button keeps its size, on the left */
  .answer {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
  }
`;

/** When: the time as on the day's list, the day written out and the status. */
const When = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;

  /* as on the day's list: the start, its length under it, a thin line before the rest */
  .time {
    display: flex;
    flex: none;
    flex-direction: column;
    justify-content: center;
    padding-right: 1rem;
    white-space: nowrap;
    color: var(--color-neutral-white);
    border-right: 1px solid var(--color-neutral-base);
  }

  .time strong {
    font-size: 1.125rem;
    font-weight: 700;
  }

  .time small {
    font-size: 0.75rem;
    color: var(--color-neutral-150);
  }

  .text {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    min-width: 0;
  }

  .day {
    font-size: 0.9375rem;
    font-weight: 700;
    color: var(--color-neutral-white);
  }

  .day::first-letter {
    text-transform: uppercase;
  }

  .note {
    font-size: 0.75rem;
    color: var(--color-neutral-150);
  }
`;

const Status = styled.span<{ $status: AgendaBookingStatus }>`
  display: inline-flex;
  gap: 0.375rem;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1rem;
  color: ${({ $status }) => STATUS_COLORS[$status]};

  &::before {
    width: 6px;
    height: 6px;
    content: "";
    background-color: currentColor;
    border-radius: 100%;
  }
`;

/** What was filled in: one frame, a row per item (its label over its value), as a contact's. */
const Rows = styled.dl`
  overflow: hidden;
  background: var(--color-neutral-darkness);
  border-radius: 1rem;

  .row {
    padding: 0.75rem 1rem;
  }

  .row + .row {
    border-top: 1px solid var(--color-neutral-base);
  }

  dt {
    font-size: 0.75rem;
    line-height: 1rem;
    color: var(--color-neutral-150);
  }

  dd {
    margin-top: 0.125rem;
    font-size: 0.9375rem;
    line-height: 1.375rem;
    color: var(--color-neutral-white);
    overflow-wrap: anywhere;
  }
`;

const Asking = styled.p`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--color-neutral-150);

  strong {
    padding: 0.75rem 1rem;
    font-weight: 700;
    color: var(--color-neutral-white);
    background: var(--color-neutral-darkness);
    border-radius: 0.75rem;
  }

  strong::first-letter {
    text-transform: uppercase;
  }
`;
